/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Divider,
  Spinner,
  Center,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";
import { formatNumber } from "../../../utils/formatNumber";

export default function Growth() {
  const { i18n } = useTranslation();
  const { isArabic, colors, dir, textAlign } = useTheme();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantSales, setTenantSales] = useState({});

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // ===========================
        // 1️⃣ جلب كل المتاجر (tenantId = -1)
        // ===========================
        const tenantsRes = await fetch(
          `${baseUrl}/GetAllTenantFullDetails?tenantId=-1`,
          {
            headers: { Accept: "application/json" },
          }
        );

        if (!tenantsRes.ok) {
          throw new Error("Failed to fetch tenants");
        }

        let tenantsRaw = await tenantsRes.text();
        let tenantsData;

        try {
          tenantsData = JSON.parse(tenantsRaw);
        } catch {
          console.error("❌ Invalid tenants response:", tenantsRaw);
          throw new Error("Invalid tenants API response");
        }

        if (!Array.isArray(tenantsData)) {
          tenantsData = [tenantsData];
        }

        // ===========================
        // 2️⃣ جلب جميع المدفوعات (tenantId = -1)
        // ===========================
        const token = localStorage.getItem("authToken");

        const paymentsRes = await fetch(
          `${baseUrl}/GetTenantPayment?tenantId=-1`,
          {
            headers: {
              Accept: "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        let payments = [];
        if (paymentsRes.ok) {
          const paymentsText = await paymentsRes.text();

          try {
            payments = JSON.parse(paymentsText);
          } catch {
            console.error("❌ Invalid payments response:", paymentsText);
            payments = [];
          }

          if (!Array.isArray(payments)) payments = [payments];
        }

        // ===========================
        // 3️⃣ حساب المبيعات لكل متجر
        // ===========================
        const salesMap = {};

        payments.forEach((p) => {
          const status =
            p.TransactionStatus ||
            p.transactionStatus ||
            p.Status ||
            p.status ||
            "";

          const isPaid =
            String(status).toLowerCase() === "paid" ||
            status === 1 ||
            status === "1";

          if (!isPaid) return;

          const rawTenantId =
            p.TenantId || p.tenantId || p.TenantID || p.tenantID;

          const tenantId =
            rawTenantId !== null && rawTenantId !== undefined
              ? String(rawTenantId)
              : null;

          const amount =
            parseFloat(
              p.TransactionAmount ||
              p.transactionAmount ||
              p.Amount ||
              p.amount
            ) || 0;

          if (tenantId && amount > 0) {
            salesMap[tenantId] = (salesMap[tenantId] || 0) + amount;
          }
        });
        const sortedTenants = [...tenantsData]
          .sort((a, b) => {
            const idA = String(a.TenantId || a.tenantId || "");
            const idB = String(b.TenantId || b.tenantId || "");
            const sA = salesMap[idA] || 0;
            const sB = salesMap[idB] || 0;
            return sB - sA;
          })
          .filter(t => {
            const id = String(t.TenantId || t.tenantId || "");
            return (salesMap[id] || 0) > 0;
          });

        setTenantSales(salesMap);


        setTenants(sortedTenants);
      } catch (err) {
        console.error("❌ Error in Growth page:", err);
        setError(err.message || "Error loading data");
        setTenants([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [baseUrl]);

  // ===========================
  //  UI
  // ===========================
  return (
    <PageContainer
      title={
        isArabic ? "تقرير المتاجر الأسرع نمواً" : "Fastest Growing Stores Report"
      }
    >
      <UnifiedCard mb={6} hoverable={false}>
        <Text fontSize="lg" color={colors.textColor} fontWeight="bold" mb={4}>
          {isArabic
            ? "تقرير عن المتاجر الأسرع نمواً (الأكثر مبيعات)"
            : "Fastest Growing Stores Report (Highest Sales)"}
        </Text>
        <Text fontSize="sm" color={colors.subText} mb={2}>
          {isArabic
            ? "هذا التقرير يعرض جميع المتاجر مرتبة حسب إجمالي المبيعات"
            : "This report displays all stores sorted by total sales"}
        </Text>
        <Text fontSize="sm" color={colors.subText} fontWeight="semibold">
          {isArabic
            ? "📊 الترتيب: من الأعلى إلى الأقل (المتاجر الأكثر مبيعات تظهر أولاً)"
            : "📊 Sorting: Highest to Lowest (Stores with most sales appear first)"}
        </Text>
      </UnifiedCard>

      {loading ? (
        <Center py={8}>
          <Spinner color={colors.mainColor} size="lg" />
        </Center>
      ) : error ? (
        <Alert status="error" borderRadius="lg" mb={4}>
          <AlertIcon />
          {isArabic
            ? "فشل الاتصال بالخادم. تأكد من أن الـ API يعمل بشكل صحيح."
            : "Failed to connect to the server. Please verify the API is running."}
        </Alert>
      ) : tenants.length ? (
        <VStack align="stretch" spacing={3}>
          {tenants.map((t, index) => {
            const id = String(t.TenantId || t.tenantId || "");
            const sales = tenantSales[id] || 0;
            const rank = index + 1;

            return (
              <UnifiedCard key={id || index}>
                <HStack justify="space-between" mb={3} flexWrap="wrap">
                  <HStack spacing={2}>
                    <Text
                      color={colors.mainColor}
                      fontWeight="bold"
                      fontSize="xl"
                      bg={colors.cardBg}
                      px={3}
                      py={1}
                      borderRadius="md"
                      borderWidth="2px"
                      borderColor={colors.mainColor}
                    >
                      #{rank}
                    </Text>

                    <Text color={colors.mainColor} fontWeight="bold" fontSize="lg">
                      {isArabic ? "المرتبة" : "Rank"}
                    </Text>
                  </HStack>

                  <Text
                    color={colors.mainColor}
                    fontWeight="bold"
                    fontSize={{ base: "md", md: "lg" }}
                  >
                    💰{" "}
                    {isArabic ? "إجمالي المبيعات:" : "Total Sales:"}{" "}
                    <Text
                      as="span"
                      color={rank <= 3 ? colors.mainColor : colors.textColor}
                    >
                      {formatNumber(sales)} {isArabic ? "د.ع" : "IQD"}
                    </Text>
                  </Text>
                </HStack>

                <Divider mb={3} />

                <VStack align="stretch" spacing={2}>
                  <Text color={colors.textColor} fontWeight="semibold" fontSize="md">
                    🏪 {t.TenantName || t.EngTenantName || "-"}
                  </Text>

                  <Text color={colors.subText} fontSize="sm">
                    📧 {t.Email || "-"}
                  </Text>

                  <Text color={colors.subText} fontSize="sm">
                    🌐 {t.SubDomain || "-"}
                  </Text>

                  <Text color={colors.subText} fontSize="sm">
                    📅 {isArabic ? "تاريخ الإنشاء:" : "Created:"}{" "}
                    {t.CreatedAt?.split("T")[0] || "-"}
                  </Text>

                  <Text color={colors.subText} fontSize="sm">
                    🔹 {isArabic ? "الحالة:" : "Status:"}{" "}
                    {t.IsActive === 1
                      ? isArabic
                        ? "نشط"
                        : "Active"
                      : isArabic
                        ? "غير مفعل"
                        : "Inactive"}
                  </Text>
                </VStack>
              </UnifiedCard>
            );
          })}
        </VStack>
      ) : (
        <Text color={colors.subText} textAlign="center">
          {isArabic ? "لا توجد متاجر" : "No tenants found"}
        </Text>
      )}
    </PageContainer>
  );
}
